import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtTime, todayStr } from "@/components/Pager";
import { toast } from "sonner";
import { FileText, Trash2, Upload, Loader2 } from "lucide-react";

const ST = { present: ["Present", "bg-emerald-100 text-emerald-800"], late: ["Late", "bg-sky-100 text-sky-800"], half_day: ["Half day", "bg-amber-100 text-amber-800"],
  absent: ["Absent", "bg-rose-100 text-rose-700"], leave: ["Leave", "bg-violet-100 text-violet-800"], sunday: ["Sunday", "bg-slate-50 text-slate-300"] };

export function StaffMonthly({ uid }) {
  const [month, setMonth] = useState(todayStr().slice(0, 7));
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/staff-mgmt/staff/${uid}/attendance`, { params: { month } }).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e))); }, [uid, month]);
  return (
    <div data-testid="staff-monthly" className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Input data-testid="staff-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />
        {d && <span data-testid="staff-month-pct" className="font-display text-xl font-bold text-violet-800">{d.attendance_pct == null ? "—" : `${d.attendance_pct}%`}</span>}
      </div>
      {d && (
        <>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center text-sm">
            {[["present", "Present"], ["late", "Late"], ["half_day", "Half day"], ["absent", "Absent"], ["leave", "Leave"], ["unmarked", "Not marked"]].map(([k, l]) => (
              <div key={k} className="bg-slate-50 rounded-xl py-2"><div className="font-bold">{d.counts[k]}</div><div className="text-[10px] text-slate-500">{l}</div></div>))}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((w) => <div key={w} className="text-[10px] text-center text-slate-400 uppercase">{w}</div>)}
            {d.days.length > 0 && Array.from({ length: (new Date(`${d.days[0].date}T00:00:00`).getDay() + 6) % 7 }, (_, i) => <div key={`b${i}`} />)}
            {d.days.map((x) => (
              <div key={x.date} data-testid={`staff-day-${x.date}`} title={x.marked_by ? `Marked by ${x.marked_by}` : ""} className={`rounded-lg p-1.5 text-center ${x.status ? ST[x.status][1] : "bg-white border border-dashed border-slate-200 text-slate-400"}`}>
                <div className="text-xs font-bold">{Number(x.date.slice(8))}</div><div className="text-[9px]">{x.status ? ST[x.status][0] : "—"}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const KINDS = [["id_proof", "ID proof"], ["certificate", "Certificate"], ["contract", "Contract"], ["other", "Other"]];

export function StaffDocs({ uid, canEdit }) {
  const [docs, setDocs] = useState([]);
  const [kind, setKind] = useState("id_proof");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = () => api.get(`/staff-mgmt/staff/${uid}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
  async function upload(file) {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind); fd.append("title", title);
    try { await api.post(`/staff-mgmt/staff/${uid}/documents`, fd); toast.success("Document uploaded"); setTitle(""); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); if (input.current) input.current.value = ""; }
  }
  async function open(d) {
    try { const r = await api.get(`/staff-mgmt/files/${d.id}`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (e) { toast.error("Could not open the file"); }
  }
  async function del(d) { if (!window.confirm(`Delete ${d.title}?`)) return; try { await api.delete(`/staff-mgmt/files/${d.id}`); load(); } catch (e) { toast.error(formatApiError(e)); } }
  return (
    <div data-testid="staff-docs" className="space-y-3">
      {canEdit && (
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={kind} onValueChange={setKind}><SelectTrigger data-testid="doc-kind" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{KINDS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          <Input data-testid="doc-title" placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} className="w-56" />
          <input ref={input} data-testid="doc-file" type="file" accept=".pdf,.doc,.docx,image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
          <Button data-testid="doc-upload" variant="outline" onClick={() => input.current?.click()} disabled={busy}>{busy ? <Loader2 size={15} className="animate-spin mr-1" /> : <Upload size={15} className="mr-1" />} Upload</Button>
        </div>
      )}
      {docs.length === 0 ? <div className="text-sm text-slate-500">No documents uploaded.</div> : docs.map((d) => (
        <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center gap-3 py-2 border-t border-slate-100 text-sm">
          <FileText size={18} className="text-violet-700" />
          <button onClick={() => open(d)} className="flex-1 text-left hover:text-violet-700"><span className="font-semibold">{d.title}</span> <span className="text-xs text-slate-400">{KINDS.find((k) => k[0] === d.kind)?.[1]} • {fmtTime(d.created_at)} • {d.uploaded_by}</span></button>
          {canEdit && <button data-testid={`doc-delete-${d.id}`} onClick={() => del(d)} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>}
        </div>
      ))}
    </div>
  );
}
